import { AxiosInstance } from "axios";
import {
  getWithAuth,
  postWithAuth,
  putWithAuth,
  deleteWithAuth,
} from "../InitializeService";
import { buildODataQuery, ODataParams } from "@/utils/odataUtil";

export interface CreatePostingReq {
  clientId: number;
  serviceType: string;
  telehealth: boolean;
  serviceRequestedBy?: string;
}
export const getPostings = (params: ODataParams, instance: AxiosInstance) => {
  return getWithAuth(`staffing/postings${buildODataQuery(params)}`, instance);
};

/** Plain array of `{ id, title, priority }` — the vocabulary for the Staffing Reasons filter. Not OData. */
export const getStaffingReasons = (instance: AxiosInstance) => {
  return getWithAuth(`staffing/reasons`, instance);
};

export const createPosting = (
  req: CreatePostingReq,
  instance: AxiosInstance,
) => {
  return postWithAuth(`staffing/postings`, req, instance);
};

export interface UpdatePostingReq {
  status: string;
  serviceRequestedBy: string | null;
}
export const updatePosting = (
  postingId: number,
  req: UpdatePostingReq,
  instance: AxiosInstance,
) => {
  return putWithAuth(`staffing/postings/${postingId}`, req, instance);
};

export const getMatchedStaffForClient = (
  clientId: number,
  region: string,
  serviceType: string,
  count: number = 10,
  instance: AxiosInstance,
) => {
  return getWithAuth(
    `staffing/matches/clients/${clientId}/staff?region=${region}&serviceType=${serviceType}&top=${count}`,
    instance,
  );
};

export const getMatchedApplicantsForClient = (
  clientId: number,
  region: string,
  serviceType: string,
  count: number = 10,
  instance: AxiosInstance,
) => {
  return getWithAuth(
    `staffing/matches/clients/${clientId}/applicants?region=${region}&serviceType=${serviceType}&top=${count}`,
    instance,
  );
};

export const getMatchedClientsForStaff = (
  loginName: string,
  region: string,
  count: number = 10,
  instance: AxiosInstance,
) => {
  return getWithAuth(
    `staffing/matches/staff/clients?loginName=${loginName}&region=${region}&top=${count}`,
    instance,
  );
};

export const getPostingMatchForStaff = (
  postingId: number,
  loginName: string,
  instance: AxiosInstance,
) => {
  return getWithAuth(
    `staffing/matches/postings/${postingId}/staff?loginName=${loginName}`,
    instance,
  );
};

export const getMatchedClientsForApplicant = (
  email: string,
  region: string,
  count: number = 10,
  instance: AxiosInstance,
) => {
  return getWithAuth(
    `staffing/matches/applicants/clients?email=${email}&region=${region}&top=${count}`,
    instance,
  );
};

export const getActivePostingForClient = (
  clientId: number,
  serviceType: string,
  instance: AxiosInstance,
) =>
  getWithAuth(
    `staffing/postings${buildODataQuery({
      filter: `ClientId eq ${clientId} and ServiceType eq '${serviceType}' and Status eq 'Active'`,
    })}`,
    instance,
  );

export interface CreateProcessReq {
  postingId: number;
  staff: string;
  source: string;
  applicant: boolean;
  author: string | null;
  adHocGroup?: AdhocGroupReq;
}
export interface AdhocGroupReq {
  clientId: number;
  clientName: string;
  staffFullName: string;
  staffRole: string;
  staffRegion: string;
}
export interface CreateProcessRes {
  id: number;
  referenceId: string;
}
export const createProcess = (req: CreateProcessReq, instance: AxiosInstance) =>
  postWithAuth("staffing/process", req, instance);

export const getProcesses = (params: ODataParams, instance: AxiosInstance) =>
    getWithAuth(`staffing/process${buildODataQuery(params)}`, instance);

export const getProcessesForPosting = (postingId: number, instance: AxiosInstance) =>
    getWithAuth(`staffing/postings${buildODataQuery({
        filter: `Id eq ${postingId}`,
        expand: ['processes']
    })}`, instance);

/**
 * Response of `POST staffing/process/{id}/reopen` (Enterprise API 102, task 13574): PBS Live
 * rehydrates the closed adhoc staffing chat (+7 days), the process row becomes `Reopened`, and
 * the scheduling slot card is re-sent when possible (`slotCardSent` null = not attempted).
 * camelCase — a plain MVC action, not an OData entity set.
 */
export interface ReopenProcessRes {
  processId: number;
  referenceId: string;
  status: string;
  chatReactivated: boolean;
  chatExpiresOn: string | null;
  messageInserted: boolean;
  slotCardSent: boolean | null;
}

/** Reopens a Closed staffing process on an Active posting and rehydrates its PBS Live chat. No body. */
export const reopenProcess = (processId: number, instance: AxiosInstance) =>
    postWithAuth(`staffing/process/${processId}/reopen`, {}, instance);

export const getNotes = (params: ODataParams, instance: AxiosInstance) => {
  return getWithAuth(`staffing/notes${buildODataQuery(params)}`, instance);
};

export interface CreateNoteReq {
  userIdentifier: string;
  userType: string;
  note: string;
  pinned: boolean;
  sendToFile?: boolean;
}

export const createNote = (req: CreateNoteReq, instance: AxiosInstance) => {
  return postWithAuth("staffing/notes", req, instance);
};

export const updateNote = (
  noteId: number,
  userType: string,
  req: { note: string; pinned: boolean },
  instance: AxiosInstance,
) => {
  return putWithAuth(
    `staffing/notes/${noteId}?userType=${userType}`,
    req,
    instance,
  );
};

export const deleteNote = (
  noteId: number,
  userType: string,
  instance: AxiosInstance,
) => {
  return deleteWithAuth(
    `staffing/notes/${noteId}?userType=${userType}`,
    instance,
  );
};

export interface AspWorkflowReq {
  staffLoginName: string;
  clientId: number;
  source: string;
  processId?: number;
}

export const runAspWorkflow = (
  req: AspWorkflowReq,
  instance: AxiosInstance,
) => {
  // ASP workflow can outlast the shared client's 2-minute default timeout (0 = no timeout)
  return postWithAuth("asp/workflow", req, instance, { timeout: 0 });
};

export const getBlockedRelationships = (params: ODataParams, instance: AxiosInstance) =>
  getWithAuth(`staffing/blockedrelationships${buildODataQuery(params)}`, instance);

export interface CreateBlockedRelationshipReq {
  clientId: number;
  staff: string;
  reason?: string;
}

export const createBlockedRelationship = (req: CreateBlockedRelationshipReq, instance: AxiosInstance) =>
  postWithAuth('staffing/blockedrelationships', req, instance);

export const deleteBlockedRelationship = (id: number, instance: AxiosInstance) =>
  deleteWithAuth(`staffing/blockedrelationships/${id}`, instance);

export const closePosting = (postingId: number, instance: AxiosInstance) =>
  postWithAuth(`staffing/postings/${postingId}/close`, {}, instance);

/**
 * Body of POST staffing/scheduling/slots/selection — the scheduling slot card's submit
 * contract. Dynamic keys: `referenceId` ({processId}-{staffLogin}-{clientId}),
 * `triggeredBy` (bare login of the person submitting) and one `wBday{0-6}` entry per
 * selected day carrying a `WB|D{day}|{startMinutes}|{endMinutes}` token.
 */
export type SlotSelectionPayload = {
  referenceId: string;
  triggeredBy: string;
} & Record<string, string>;

/** PBS Live toast contract: `message` on 2xx, `error` on 4xx/5xx. */
export interface SlotSelectionResponse {
  message?: string;
  error?: string;
}

export const saveScheduleSlotSelection = (
  payload: SlotSelectionPayload,
  instance: AxiosInstance,
) => postWithAuth("staffing/scheduling/slots/selection", payload, instance);

export interface PbsLiveInsertionReq {
  pbsLiveInsertionsId: number;
  identifierValue: string;
  receiversRegion: string;
  dynamicTags: Record<string, string>;
  retry: boolean;
}

export const createPbsLiveInsertion = (
  req: PbsLiveInsertionReq,
  instance: AxiosInstance,
) => {
  return postWithAuth("pbslive/insertions", req, instance);
};
